import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { InputTextModule } from 'primeng/inputtext';
import { MockChatService, Chat, ChatMessage } from '../../services-mock/mock-chat.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-chat-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    AvatarModule,
    InputTextModule,
  ],
  template: `
    <div class="chat-detail-container" *ngIf="chat">
      <div class="chat-header">
        <button
          pButton
          type="button"
          icon="pi pi-arrow-left"
          label="Back"
          (click)="goBack()"
          class="back-button"
        ></button>
        <div class="chat-participant">
          <p-avatar
            [label]="chat.participantInitials"
            shape="circle"
            size="normal"
          ></p-avatar>
          <div class="participant-info">
            <div class="participant-name">{{ chat.participantName }}</div>
            <div class="participant-status">Online</div>
          </div>
        </div>
      </div>

      <div class="chat-messages" #messagesContainer>
        <div
          *ngFor="let message of chat.messages"
          class="message"
          [class.sent]="message.senderId === 'current-user'"
          [class.received]="message.senderId !== 'current-user'"
        >
          <div class="message-content">
            <div class="message-avatar" *ngIf="message.senderId !== 'current-user'">
              <p-avatar
                [label]="message.senderInitials"
                shape="circle"
                size="normal"
              ></p-avatar>
            </div>
            <div class="message-bubble">
              <div class="message-text">{{ message.text }}</div>
              <div class="message-time">{{ formatMessageTime(message.timestamp) }}</div>
            </div>
            <div class="message-avatar" *ngIf="message.senderId === 'current-user'">
              <p-avatar
                [label]="currentUserInitials"
                shape="circle"
                size="normal"
              ></p-avatar>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-input-container">
        <input
          type="text"
          pInputText
          [(ngModel)]="newMessage"
          (keyup.enter)="sendMessage()"
          placeholder="Type a message..."
          class="chat-input"
        />
        <button
          pButton
          type="button"
          icon="pi pi-send"
          (click)="sendMessage()"
          [disabled]="!newMessage.trim()"
          class="send-button"
        ></button>
      </div>
    </div>

    <div class="chat-detail-container" *ngIf="!chat && !loading">
      <div class="error-message">
        <h2>Chat Not Found</h2>
        <p>The chat you're looking for doesn't exist or has been removed.</p>
        <button pButton type="button" label="Back to Chats" (click)="goBack()"></button>
      </div>
    </div>
  `,
  styles: [
    `
      .chat-detail-container {
        display: flex;
        flex-direction: column;
        height: calc(100vh - 60px);
        max-width: 1200px;
        margin: 0 auto;
        background-color: #ffffff;
      }

      .chat-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid #e5e5e5;
        background-color: #ffffff;
      }

      .back-button {
        background: transparent;
        color: #666666;
        border: 1px solid #e5e5e5;
      }

      .chat-participant {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex: 1;
      }

      .participant-info {
        display: flex;
        flex-direction: column;
      }

      .participant-name {
        font-weight: 600;
        font-size: 1rem;
        color: #000000;
      }

      .participant-status {
        font-size: 0.85rem;
        color: #4caf50;
      }

      .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .message {
        display: flex;
        width: 100%;
      }

      .message.sent {
        justify-content: flex-end;
      }

      .message.received {
        justify-content: flex-start;
      }

      .message-content {
        display: flex;
        align-items: flex-end;
        gap: 0.5rem;
        max-width: 70%;
      }

      .message.sent .message-content {
        flex-direction: row-reverse;
      }

      .message-bubble {
        background-color: #ffffff;
        border-radius: 12px;
        padding: 0.75rem 1rem;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      .message.sent .message-bubble {
        background-color: #0066cc;
        color: #ffffff;
      }

      .message-text {
        font-size: 0.95rem;
        line-height: 1.4;
        margin-bottom: 0.25rem;
      }

      .message-time {
        font-size: 0.75rem;
        opacity: 0.7;
      }

      .message-avatar {
        flex-shrink: 0;
      }

      .chat-input-container {
        display: flex;
        gap: 0.5rem;
        padding: 1rem 1.5rem;
        border-top: 1px solid #e5e5e5;
        background-color: #ffffff;
      }

      .chat-input {
        flex: 1;
      }

      .send-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .send-button:disabled {
        background: #cccccc;
        cursor: not-allowed;
      }

      .error-message {
        text-align: center;
        padding: 3rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .error-message h2 {
        color: #000000;
        margin-bottom: 1rem;
      }

      .error-message p {
        color: #666666;
        margin-bottom: 2rem;
      }

      :host ::ng-deep {
        .p-avatar {
          width: 40px;
          height: 40px;
        }

        .message-avatar .p-avatar {
          width: 32px;
          height: 32px;
        }
      }
    `,
  ],
})
export class ChatDetailComponent implements OnInit {
  chat: Chat | undefined;
  loading = true;
  newMessage = '';
  currentUserInitials = 'U';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private chatService: MockChatService,
    private authService: AuthService
  ) {
    this.currentUserInitials = this.authService.getUserInitials();
  }

  ngOnInit() {
    const chatId = this.route.snapshot.params['id'];
    if (chatId) {
      this.chatService.getChat(chatId).subscribe((chat) => {
        this.chat = chat;
        this.loading = false;
        setTimeout(() => this.scrollToBottom(), 100);
      });
    } else {
      this.loading = false;
    }
  }

  sendMessage() {
    if (!this.newMessage.trim() || !this.chat) {
      return;
    }

    this.chatService.sendMessage(this.chat.id, this.newMessage).subscribe((message) => {
      if (this.chat) {
        this.chat.messages.push(message);
        this.chat.lastMessage = message.text;
        this.chat.lastMessageTime = message.timestamp;
      }
      this.newMessage = '';
      setTimeout(() => this.scrollToBottom(), 100);
    });
  }

  scrollToBottom() {
    const container = document.querySelector('.chat-messages');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  goBack() {
    this.router.navigate(['/chats']);
  }

  formatMessageTime(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
  }
}

